import React, { useState } from 'react';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaEye, FaEyeSlash, FaUser } from 'react-icons/fa';
import testimonialsService from '../../services/testimonials.service';

const TestimonialList = ({ testimonials, onEdit, onRefresh }) => {
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      setDeletingId(id);
      try {
        await testimonialsService.deleteTestimonial(id);
        onRefresh();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        alert('Failed to delete testimonial');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleToggleActive = async (id) => {
    setTogglingId(id);
    try {
      await testimonialsService.toggleTestimonialActive(id);
      onRefresh();
    } catch (error) {
      console.error('Error toggling testimonial:', error);
      alert('Failed to toggle testimonial status');
    } finally {
      setTogglingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!testimonials.length) {
    return (
      <EmptyState>
        <EmptyIcon>📝</EmptyIcon>
        <EmptyTitle>No Testimonials Yet</EmptyTitle>
        <EmptyText>Start building trust by adding testimonials from your ministry members.</EmptyText>
      </EmptyState>
    );
  }

  return (
    <ListContainer>
      <ListHeader>
        <h3>Manage Testimonials</h3>
        <TestimonialCount>{testimonials.length} testimonial{testimonials.length !== 1 ? 's' : ''}</TestimonialCount>
      </ListHeader>

      <TestimonialsGrid>
        {testimonials.map((testimonial) => (
          <TestimonialCard key={testimonial._id}>
            <CardHeader>
              <ProfileSection>
                {testimonial.image ? (
                  <ProfileImage src={testimonial.image} alt={testimonial.name} />
                ) : (
                  <DefaultAvatar>
                    <FaUser />
                  </DefaultAvatar>
                )}
                <ProfileInfo>
                  <ProfileName>{testimonial.name}</ProfileName>
                  <ProfileRole>{testimonial.role}</ProfileRole>
                  <ProfileMinistry>{testimonial.ministry || 'General Member'}</ProfileMinistry>
                </ProfileInfo>
              </ProfileSection>
              
              <StatusBadge $isActive={testimonial.isActive}>
                {testimonial.isActive ? 'Active' : 'Inactive'}
              </StatusBadge>
            </CardHeader>

            <TestimonialText>
              "{testimonial.testimony.length > 150 
                ? `${testimonial.testimony.substring(0, 150)}...` 
                : testimonial.testimony}"
            </TestimonialText>

            <CardFooter>
              <DateInfo>Added: {formatDate(testimonial.createdAt)}</DateInfo>
              
              <ActionButtons>
                <ActionButton
                  onClick={() => onEdit(testimonial)}
                  title="Edit testimonial"
                  $variant="edit"
                >
                  <FaEdit />
                </ActionButton>
                
                <ActionButton
                  onClick={() => handleToggleActive(testimonial._id)}
                  title={testimonial.isActive ? 'Deactivate' : 'Activate'}
                  $variant="toggle"
                  disabled={togglingId === testimonial._id}
                >
                  {togglingId === testimonial._id ? (
                    '...'
                  ) : testimonial.isActive ? (
                    <FaEyeSlash />
                  ) : (
                    <FaEye />
                  )}
                </ActionButton>
                
                <ActionButton
                  onClick={() => handleDelete(testimonial._id)}
                  title="Delete testimonial"
                  $variant="delete"
                  disabled={deletingId === testimonial._id}
                >
                  {deletingId === testimonial._id ? '...' : <FaTrash />}
                </ActionButton>
              </ActionButtons>
            </CardFooter>
          </TestimonialCard>
        ))}
      </TestimonialsGrid>
    </ListContainer>
  );
};

const ListContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f8f9fa;

  h3 {
    color: ${props => props.theme.colors.primary};
    margin: 0;
    font-size: 1.5rem;
  }
`;

const TestimonialCount = styled.span`
  background: ${props => props.theme.colors.primary}22;
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.div`
  border: 2px solid #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary}44;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const ProfileImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${props => props.theme.colors.primary}22;
`;

const DefaultAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary}22;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  border: 3px solid ${props => props.theme.colors.primary}22;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

const ProfileRole = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ProfileMinistry = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.8rem;
`;

const StatusBadge = styled.span`
  background: ${props => props.$isActive ? '#28a74522' : '#dc354522'};
  color: ${props => props.$isActive ? '#28a745' : '#dc3545'};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
`;

const TestimonialText = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin: 1rem 0;
  font-style: italic;
  font-size: 0.95rem;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f8f9fa;
`;

const DateInfo = styled.span`
  color: #6c757d;
  font-size: 0.8rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  ${props => {
    switch (props.$variant) {
      case 'edit':
        return `
          background: #17a2b822;
          color: #17a2b8;
          &:hover:not(:disabled) {
            background: #17a2b8;
            color: white;
          }
        `;
      case 'toggle':
        return `
          background: #ffc10722;
          color: #ffc107;
          &:hover:not(:disabled) {
            background: #ffc107;
            color: white;
          }
        `;
      case 'delete':
        return `
          background: #dc354522;
          color: #dc3545;
          &:hover:not(:disabled) {
            background: #dc3545;
            color: white;
          }
        `;
      default:
        return '';
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
`;

const EmptyText = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.6;
`;

export default TestimonialList;
